import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { CheckCircle2, Package, Truck, Home as HomeIcon, MapPin } from "lucide-react";

const STATUSES = [
  { key: "confirmed", label: "CONFIRMED", icon: CheckCircle2 },
  { key: "packed", label: "PACKED", icon: Package },
  { key: "shipped", label: "SHIPPED", icon: Truck },
  { key: "out_for_delivery", label: "OUT FOR DELIVERY", icon: MapPin },
  { key: "delivered", label: "DELIVERED", icon: HomeIcon },
];

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data.order));
  }, [id]);

  if (!order) return <div className="py-24 text-center font-display tracking-widest">LOADING ORDER…</div>;

  const currentIdx = STATUSES.findIndex((s) => s.key === order.status);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="order-detail-page">
      <div className="text-center mb-12">
        <CheckCircle2 size={48} className="mx-auto text-[#FF3B30] mb-4" />
        <h1 className="font-display text-5xl tracking-tighter">ORDER CONFIRMED</h1>
        <p className="text-black/50 mt-2 font-mono text-sm">ORDER NO: {order.order_no}</p>
        {order.pricing?.points_earned > 0 && (
          <p className="text-[#FF3B30] mt-2 font-display tracking-widest text-sm">+{order.pricing.points_earned} ELMNTS POINTS EARNED</p>
        )}
      </div>

      <div className="border border-black/15 p-6 mb-8">
        <h2 className="font-display text-2xl tracking-tighter mb-6">TRACKING</h2>
        <div className="grid grid-cols-5 gap-2">
          {STATUSES.map((s, i) => {
            const Icon = s.icon;
            const active = i <= currentIdx;
            return (
              <div key={s.key} className="text-center">
                <div className={`mx-auto w-12 h-12 border ${active ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "border-black/20 text-black/30"} flex items-center justify-center`}>
                  <Icon size={18} />
                </div>
                <div className={`mt-2 font-display tracking-widest text-[10px] ${active ? "text-black" : "text-black/30"}`}>{s.label}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 space-y-2">
          {order.tracking?.map((t) => (
            <div key={`${t.status}-${t.at}`} className="flex justify-between text-xs font-mono text-black/60 border-b border-black/10 pb-2">
              <span>{t.status.toUpperCase()} — {t.note}</span>
              <span>{new Date(t.at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="border border-black/15 p-6">
          <h3 className="font-display tracking-widest text-xs text-black/60 mb-3">SHIPPING TO</h3>
          <div className="text-sm space-y-1">
            <div className="font-semibold">{order.address.full_name}</div>
            <div className="text-black/70">{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</div>
            <div className="text-black/70">{order.address.city}, {order.address.state} - {order.address.pincode}</div>
            <div className="text-black/70">📞 {order.address.phone}</div>
          </div>
        </div>
        <div className="border border-black/15 p-6">
          <h3 className="font-display tracking-widest text-xs text-black/60 mb-3">PAYMENT</h3>
          <div className="text-sm space-y-1">
            <div>{order.payment_method}</div>
            <div className="text-[#FF3B30] font-display tracking-widest text-xs">✓ {order.payment_status}</div>
            <div className="text-black/70">Total: {formatINR(order.pricing.total)}</div>
          </div>
        </div>
      </div>

      <div className="border border-black/15 p-6 mb-8">
        <h3 className="font-display text-2xl tracking-tighter mb-4">ITEMS</h3>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={`${it.product_id}-${it.size}-${it.color}-${i}`} className="grid grid-cols-[64px_1fr_auto] gap-4 items-center border-b border-black/10 pb-3">
              <img src={it.image} alt="" className="w-16 aspect-[3/4] object-cover" />
              <div>
                <div className="font-display tracking-tight uppercase">{it.name}</div>
                <div className="text-xs font-mono text-black/40">SIZE {it.size} · {it.color} · QTY {it.qty}</div>
              </div>
              <div className="font-display">{formatINR(it.price * it.qty)}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1 text-sm">
          <Row l="SUBTOTAL" v={formatINR(order.pricing.subtotal)} />
          {order.pricing.discount > 0 && <Row l="DISCOUNT" v={`-${formatINR(order.pricing.discount)}`} />}
          {order.pricing.wallet_redeem > 0 && <Row l="POINTS USED" v={`-${formatINR(order.pricing.wallet_redeem)}`} />}
          <Row l="SHIPPING" v={order.pricing.shipping_fee === 0 ? "FREE" : formatINR(order.pricing.shipping_fee)} />
          <div className="border-t border-black/15 pt-2 mt-2">
            <Row l="TOTAL" v={formatINR(order.pricing.total)} big />
          </div>
        </div>
      </div>

      <div className="text-center">
        <Link to="/orders" className="inline-block border border-black px-8 py-4 font-display tracking-widest text-sm hover-invert mr-4">VIEW ALL ORDERS</Link>
        <Link to="/shop/all" className="inline-block bg-black text-white px-8 py-4 font-display tracking-widest text-sm">KEEP SHOPPING</Link>
      </div>
    </div>
  );
}

function Row({ l, v, big }) {
  return (
    <div className={`flex justify-between ${big ? "font-display text-lg" : ""}`}>
      <span className={big ? "" : "text-black/60"}>{l}</span>
      <span>{v}</span>
    </div>
  );
}
